import { AbstractBlockFactory } from '../block/AbstractBlockFactory';
import {
    AllowedMeasureFieldAggregations,
    BlockType,
    ComponentSettingsChartDataQueries,
    DatasetField,
    DatasetFieldValueType,
    NewBlockContextType,
    OrderDirection,
    SimpleFormattingRule,
    TextAlignType,
    AllowedFonts,
    ComponentSettingsDatasetField,
    ComponentSettingsDatasetSortField,
    ComponentSettingsTableLike,
    FieldTextFormatting,
} from '@report-content';
import { YNValues } from 'tools/types';

export interface ComponentSettingsPivotField extends ComponentSettingsDatasetField {
    sortDirection: OrderDirection;
    headerSettings: FieldTextFormatting;
    subtotalSettings: FieldTextFormatting;
    compute_result_total_ind: YNValues;
    sub_total_label: string;
    sub_total_placement: 'top' | 'bottom';
    formattingRules: Array<SimpleFormattingRule>;
    type:
        | 'total'
        | 'row_subtotal'
        | 'column_subtotal'
        | 'column_total'
        | 'row_total'
        | 'measure'
        | 'ui'
        | 'row'
        | 'column';
}

export class BlockFactoryPivot extends AbstractBlockFactory<ComponentSettingsPivot> {
    public static makeField(f: DatasetField): ComponentSettingsDatasetField {
        return {
            columnSize: 200,
            column_name: f.column_name,
            aggregation_method: '',
            dataset_column_id: Number(f.dataset_column_id),
            dataset_filter_column_id: Number(f.dataset_filter_column_id),
            dataset_filter_id: Number(f.dataset_filter_id),
            dataset_id: Number(f.dataset_id),
            display_mask_id: f.display_mask_id,
            field: f.column_name,
            id: f.reference_name,
            reference_name: f.reference_name,
            reference_name_escaped: f.reference_name_escaped,
            show_column_in_table_display_ind: 'Y',
            value_type: f.value_type,
            override_header_ind: 'N',
            break_column: 'N',
            group_column: 'N',
            compute_result_total_ind: 'Y',
        };
    }

    public getNewBlock({
        nextUId,
        nextSortOrder,
        datasetFields,
    }: NewBlockContextType): BlockType<ComponentSettingsPivot> {
        const fields: Array<ComponentSettingsDatasetField> = datasetFields.map((f) => BlockFactoryPivot.makeField(f));

        return {
            // TODO:
            component: {
                datasetRendererBlockComponentId: nextUId,
                datasetRendererBlockId: nextUId,
                datasetRendererComponentId: nextUId,
                displayOrder: nextSortOrder,
                internalName: 'pivot',
                settings: {
                    defaultEmptyValue: '',
                    is_preview_ind: 'N',
                    is_collapsed_ind: 'N',
                    title: '',
                    height: 600,
                    show_in_viewer: 'Y',
                    include_in_email: 'Y',
                    include_in_email_attachment: 'Y',
                    autoStretch: true,
                    internal_name: 'pivot',
                    fields: fields,
                    mainFont: AllowedFonts[0].value,
                    mainFontSize: 12,
                    zebra_striping: 'Y',
                    isOnCreation: true,
                    rowFields: [],
                    columnFields: [],
                    uiFields: [],
                    measureFields: [],
                    sortFields: [],
                    column_total_label: 'Overall Total',
                    column_total_placement: 'bottom',
                    show_column_totals: 'Y',
                    show_column_totals_as_percent: 'N',
                    row_total_label: 'Total',
                    show_row_totals: 'Y',
                    show_row_totals_as_percent: 'N',
                    uid: 'block_uid_' + nextUId,
                    columnTotalsSettings: {
                        textBold: true,
                        textAlign: 'right',
                    },
                    rowTotalsSettings: {
                        textBold: true,
                        textAlign: 'right',
                    },
                    fixedColumns: 0,
                    currentInnerWidth: 0,
                    groupFilter: { operator: 'AND', rules: [] },
                    dataQueries: {
                        table: {
                            resultFieldAggregations: {},
                            resultFields: [],
                            sort: [],
                        },
                    },
                },
            },
            // datasetRendererBlockId: number;
            displayOrder: nextSortOrder,
            // elementId: number;
            uid: 'block_uid_' + nextUId,
        };
    }
}

export type PivotInitialDataResponse = Array<Array<PivotDataCell>>;
export interface PivotDataCell {
    reference_name: string;
    shift: number;
    colspan?: number;
    style: PivotDataCellStyle;
    type:
        | 'total'
        | 'row_subtotal'
        | 'column_subtotal'
        | 'column_total'
        | 'row_total'
        | 'measure'
        | 'ui'
        | 'row'
        | 'column';
    uid: string;
    value: string | number;
    'value-raw': string | number;
}
export interface PivotDataCellStyle {
    columnWidth?: number;
    bgColor?: string;
    textColor?: string;
    textAlign?: TextAlignType;
    textWrap?: boolean;
    [key: string]: any;
}

export interface ComponentSettingsPivotMeasureField {
    dataset_column_id: number;
    dataset_filter_column_id: number;
    dataset_filter_id: number;
    dataset_id: number;
    aggregation_function: string;
    display_mask_id: string;
    display_type: string;
    transform_label?: string;
    reference_name: string;
    reference_name_escaped: string;
    headerSettings: FieldTextFormatting;
    subtotalSettings: FieldTextFormatting;
    // dataset_filter_column_id: number;
    // dataset_filter_id: number;
    // dataset_column_id: number;
    // dataset_id: number;
    // is_result_ind: YNValues;
    // aggregate_function: string;
    column_name: string;
    // reference_name: 'sales';
    value_type: DatasetFieldValueType;
    // column_instance: 'current';
    // is_derived_ind: YNValues;
    // display_mask_id: '0';
    // column_sequence: 3;
    // name: 'sales';
    // reference_name_escaped: 'sales';
    // id: string;
    field: string;
    // instance: 'current';
    // aggregation: false;
    label: string;
    // label_new: 'sales';
    // key: 'sales-current';
    // ref_column_name: 'sales';
    sortDirection: OrderDirection;
    sub_total_label: string;
    compute_result_total_ind: YNValues;
    formattingRules: Array<SimpleFormattingRule>;
    // aggregation_function: 'Sum';
    bgColor?: string;
    textWrap?: boolean;
    textBold?: boolean;
    textItalic?: boolean;
    textColor?: string;
    textAlign?: TextAlignType;
}

export interface ComponentSettingsPivotUIField {
    columnSize: number;
    column_sequence: string | number;
    field_type: string;
    reference_name: string | number;
    bgColor?: string;
    textWrap?: boolean;
    textBold?: boolean;
    textItalic?: boolean;
    textColor?: string;
    textAlign?: TextAlignType;
}

export interface ComponentSettingsPivot extends ComponentSettingsTableLike {
    // colorsData: [];
    columnFields: Array<ComponentSettingsPivotField>;
    // columnHidden: [];
    columnTotalsSettings: FieldTextFormatting;
    column_total_label: string;
    column_total_placement: 'bottom' | 'top';
    // component_name: 'Pivot Table';
    dataQueries: ComponentSettingsChartDataQueries;
    defaultEmptyValue: string | number;
    // fields: [];
    fixedColumns: number;
    // height: 600;
    // include_in_email: 'Y';
    // include_in_email_attachment: 'Y';
    // internal_name: 'pivot';
    // is_first_apply: true;
    // is_preview_ind: 'Y';
    // mainFont: 'font-arial';
    // mainFontSize: '12';
    measureFields: Array<ComponentSettingsPivotMeasureField>;
    // no_data_label: 'No Data';
    // no_data_label_settings: { textAlign: 'left' };
    // report_multiple_units_label: 'rows';
    // report_no_units_label: 'No rows';
    // report_single_unit_label: 'row';
    rowFields: Array<ComponentSettingsPivotField>;
    rowTotalsSettings: FieldTextFormatting;
    show_column_totals: YNValues;
    show_column_totals_as_percent: YNValues;
    show_row_totals: YNValues;
    show_row_totals_as_percent: YNValues;
    row_total_label: string;
    // show_title: 'N';
    uiFields: Array<ComponentSettingsPivotUIField>;
    currentInnerWidth?: number;
    sortFields: Array<ComponentSettingsDatasetSortField>;
}

export function fieldToPivotMeasuredField(field: DatasetField): ComponentSettingsPivotMeasureField {
    const aggregations = AllowedMeasureFieldAggregations.filter((f) => f.value_type == field.value_type);

    // TODO:
    return {
        dataset_column_id: field.dataset_column_id,
        dataset_filter_column_id: field.dataset_filter_column_id,
        dataset_filter_id: field.dataset_filter_id,
        dataset_id: field.dataset_id,
        aggregation_function: aggregations.length ? aggregations[0].name : '',
        display_mask_id: field.display_mask_id ?? '0',
        display_type: '',
        reference_name: field.reference_name,
        reference_name_escaped: field.reference_name_escaped,
        column_name: field.column_name,
        value_type: field.value_type,
        headerSettings: {},
        subtotalSettings: {},
        sortDirection: 'ASC',
        compute_result_total_ind: 'Y',
        sub_total_label: 'Total',
        label: '',
        field: '',
        formattingRules: [],
        // uId: generateMeasureFieldId(),
        // value_type: field.value_type,
        // y_axis: 'left',
    };
}
